/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 * Licensed under the VIJAYA 1.0 License.
 *
 * WebhookFieldMaskPicker
 *
 * Renders inside a "Call Webhook" action config block.
 * Lets the admin pick:
 *   1. Which table (if the event covers multiple tables)
 *   2. Which columns to include as record_hints in the payload
 *
 * Stores into action config:
 *   field_mask_table_id  — number | null
 *   field_mask           — string[]  (column names, not IDs)
 *
 * Payload shape the dispatcher will send:
 * {
 *   id:        "<uuid>",
 *   event:     "record.created",
 *   actor:     { user_id, username, full_name, email },
 *   data:      { table: "contacts", record_ids: [42], records: [{ id: 42, ... }] },
 *   meta:      { org_id, app_id }
 * }
 */

import React, { useEffect, useState, useCallback } from 'react';
import APIHelper from '../../api/APIHelper';
import envConfig from '../../config/envConfig';
import { useAlertDialog } from '../common/AlertDialogContext';

const apiHelper = new APIHelper({
    apiUrl: envConfig.backendApiUrl,
    tokenUrl: envConfig.backendTokenUrl,
    clientId: envConfig.clientKey,
    clientSecret: envConfig.clientSecret,
});

// ─── Identity fields auto-checked by default ─────────────────────────────────
// When a table's columns are loaded, any column whose name matches one of these
// patterns will be pre-selected so the admin gets sensible defaults without
// hard-coding field names.
const IDENTITY_PATTERNS = [
    /^(full_?name|firstname|first_name|lastname|last_name|name)$/i,
    /^email$/i,
    /^phone(_number)?$/i,
    /^mobile$/i,
    /^username$/i,
];

function isIdentityField(colName) {
    return IDENTITY_PATTERNS.some(p => p.test(colName));
}

// ─── Props ────────────────────────────────────────────────────────────────────
// selectedEvent    — full event object from events[]
// allTables        — tables[] from parent state (already fetched)
// actionConfig     — act.config for this webhook action
// onConfigChange   — (key, value) => void  — same as parent's updateActionConfig(idx, ...)
// theme            — theme context object
// ─────────────────────────────────────────────────────────────────────────────

export default function WebhookFieldMaskPicker({
    selectedEvent,
    allTables,
    actionConfig,
    onConfigChange,
    theme,
    applicationId,
}) {
    // Columns for the currently selected table { [tableId]: Column[] }
    const [columnsCache, setColumnsCache] = useState({});
    const [loadingColumns, setLoadingColumns] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const { showAlertDialog } = useAlertDialog();
    // ── Derive which tables are in scope for this event ───────────────────────
    const scopedTables = React.useMemo(() => {
        if (!selectedEvent || selectedEvent.event_type !== 'record') return [];
        if (selectedEvent.table_scope === 'all') return allTables;
        const ids = new Set(selectedEvent.scoped_tables || []);
        return allTables.filter(t => ids.has(t.id));
    }, [selectedEvent, allTables]);

    const isSingleTable = scopedTables.length === 1;

    // Auto-select the only table if there's exactly one
    useEffect(() => {
        if (isSingleTable && !actionConfig.field_mask_table_id) {
            onConfigChange('field_mask_table_id', scopedTables[0].id);
        }
    }, [isSingleTable, scopedTables, actionConfig.field_mask_table_id, onConfigChange]);

    // ── Fetch columns for selected table ─────────────────────────────────────
    const fetchColumns = useCallback(async (tableId) => {
        if (!tableId || columnsCache[tableId]) return;
        const tableObj = allTables.find(t => t.id === tableId || t.id === parseInt(tableId, 10));
        if (!tableObj) return;
        setLoadingColumns(true);
        try {
            const resp = await apiHelper.apiCall(
                '/arcee/application/table/details/',
                {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        application_id: parseInt(applicationId, 10),
                        table_name: tableObj.name,
                    }),
                }
            );
            if (resp.ok) {
                const data = await resp.json();
                const cols = data.fields || [];
                setColumnsCache(prev => ({ ...prev, [tableId]: cols }));

                // Auto-check identity fields if field_mask is not set yet
                if (!actionConfig.field_mask || actionConfig.field_mask.length === 0) {
                    const defaults = cols
                        .filter(c => isIdentityField(c.name))
                        .map(c => c.name);
                    if (defaults.length > 0) {
                        onConfigChange('field_mask', defaults);
                    }
                }
            }
        } catch (e) {
            console.error('WebhookFieldMaskPicker: failed to fetch columns', e);
        } finally {
            setLoadingColumns(false);
        }
    }, [columnsCache, actionConfig.field_mask, onConfigChange, allTables, applicationId]);

    useEffect(() => {
        if (actionConfig.field_mask_table_id) {
            fetchColumns(actionConfig.field_mask_table_id);
        }
    }, [actionConfig.field_mask_table_id, fetchColumns]);

    // ── Column selection helpers ──────────────────────────────────────────────
    const currentMask = actionConfig.field_mask || [];
    const currentAliases = actionConfig.field_mask_aliases || {};
    const columns = columnsCache[actionConfig.field_mask_table_id] || [];

    const filteredColumns = columns.filter(c =>
        c.name.toLowerCase().includes(searchQuery.toLowerCase())
    );

    function toggleField(colName) {
        if (currentMask.includes(colName)) {
            onConfigChange('field_mask', currentMask.filter(f => f !== colName));
            const updated = { ...currentAliases };
            delete updated[colName];
            onConfigChange('field_mask_aliases', updated);
        } else {
            onConfigChange('field_mask', [...currentMask, colName]);
        }
    }

    function updateAlias(colName, value) {
        const updated = { ...currentAliases };
        if (value.trim()) {
            updated[colName] = value.trim();
        } else {
            delete updated[colName];
        }
        onConfigChange('field_mask_aliases', updated);
    }

    // function selectAll() {
    //     onConfigChange('field_mask', columns.map(c => c.name));
    // }

    function deselectAll() {
        onConfigChange('field_mask', []);
        onConfigChange('field_mask_aliases', {});
    }

    // Only render for record events
    if (!selectedEvent || selectedEvent.event_type !== 'record') return null;
    if (scopedTables.length === 0) return null;

    const selectedTableObj = scopedTables.find(t => t.id === actionConfig.field_mask_table_id);
    const allSelected = columns.length > 0 && columns.every(c => currentMask.includes(c.name));
    const someSelected = currentMask.length > 0 && !allSelected;

    function selectAll() {
        showAlertDialog({
            title: "Confirm Selection",
            message: "Please verify the fields before selecting as it may contain sensitive information.",
            type: "warning", // optional (depends on your AlertDialog UI)
            okLabel: "Proceed",
            cancelLabel: "Discard",
            showCancel: true,

            onOk: () => {
                onConfigChange('field_mask', columns.map(c => c.name));
            },

            onCancel: () => {
                // optional: do nothing
            }
        });
    }

    return (
        <div
            className="mt-3 rounded border"
            style={{ background: '#f8fafc', borderColor: '#e2e8f0', overflow: 'hidden' }}
        >
            {/* ── Section header ── */}
            <div
                className="px-3 py-2 d-flex align-items-center gap-2 border-bottom"
                style={{ background: theme.primary + '10', borderColor: '#e2e8f0' }}
            >
                <i className="bi bi-layout-three-columns" style={{ color: theme.primary, fontSize: 14 }}></i>
                <span className="fw-bold small" style={{ color: theme.primary }}>
                    Payload Field Mask
                </span>
                <span
                    className="ms-auto badge"
                    style={{
                        background: currentMask.length > 0 ? theme.primary + '20' : '#f1f5f9',
                        color: currentMask.length > 0 ? theme.primary : '#94a3b8',
                        fontSize: 10, fontWeight: 600,
                    }}
                >
                    {currentMask.length} field{currentMask.length !== 1 ? 's' : ''} selected
                </span>
            </div>

            <div className="p-3">
                {/* ── Explanation ── */}
                <p className="text-muted mb-3" style={{ fontSize: 12, lineHeight: 1.5 }}>
                    Select columns to include in <code>data.records</code> in the webhook payload.
                    The selected table also scopes this webhook — it will only fire for events
                    on that table. Data is fetched at runtime from the triggering record.
                </p>

                {/* ── Table picker (only when multiple tables are in scope) ── */}
                {!isSingleTable && (
                    <div className="mb-3">
                        <label className="form-label small fw-bold text-muted mb-1">
                            Table <span className="text-danger">*</span>
                        </label>
                        <select
                            className="form-select form-select-sm"
                            value={actionConfig.field_mask_table_id || ''}
                            onChange={e => {
                                onConfigChange('field_mask_table_id', parseInt(e.target.value, 10) || null);
                                onConfigChange('field_mask', []);
                                onConfigChange('field_mask_aliases', {});
                                setSearchQuery('');
                            }}
                            style={{ borderRadius: theme.buttonRadius }}
                        >
                            <option value="">Select table…</option>
                            {scopedTables.map(t => (
                                <option key={t.id} value={t.id}>{t.name}</option>
                            ))}
                        </select>
                        {selectedTableObj && (
                            <div className="mt-1" style={{ fontSize: 11, color: '#64748b' }}>
                                <i className="bi bi-table me-1"></i>
                                Showing columns from <strong>{selectedTableObj.name}</strong>
                            </div>
                        )}
                    </div>
                )}

                {/* Single-table label */}
                {isSingleTable && (
                    <div className="mb-3 d-flex align-items-center gap-2" style={{ fontSize: 12 }}>
                        <i className="bi bi-table" style={{ color: theme.primary }}></i>
                        <span className="text-muted">From table:</span>
                        <span className="fw-semibold text-dark">{scopedTables[0].name}</span>
                    </div>
                )}

                {/* ── Column picker ── */}
                {actionConfig.field_mask_table_id && (
                    <>
                        {loadingColumns ? (
                            <div className="text-center py-3 text-muted small">
                                <div className="spinner-border spinner-border-sm me-2" style={{ color: theme.primary }} />
                                Loading columns…
                            </div>
                        ) : columns.length === 0 ? (
                            <div className="text-center py-3 text-muted small bg-light rounded border">
                                <i className="bi bi-inbox d-block mb-1 fs-5 opacity-50"></i>
                                No columns found for this table.
                            </div>
                        ) : (
                            <>
                                {/* Toolbar: search + select all */}
                                <div className="d-flex align-items-center gap-2 mb-2">
                                    <div className="input-group input-group-sm flex-grow-1">
                                        <span className="input-group-text bg-white">
                                            <i className="bi bi-search text-muted" style={{ fontSize: 11 }}></i>
                                        </span>
                                        <input
                                            className="form-control"
                                            placeholder="Search columns…"
                                            value={searchQuery}
                                            onChange={e => setSearchQuery(e.target.value)}
                                            style={{ borderRadius: '0 4px 4px 0' }}
                                        />
                                    </div>
                                    <button
                                        className="btn btn-sm btn-light border flex-shrink-0"
                                        style={{ fontSize: 11, whiteSpace: 'nowrap' }}
                                        onClick={allSelected ? deselectAll : selectAll}
                                        title={allSelected ? 'Deselect all' : 'Select all columns'}
                                    >
                                        <i className={`bi ${allSelected ? 'bi-square' : 'bi-check-all'} me-1`}></i>
                                        {allSelected ? 'Deselect all' : 'Select all'}
                                    </button>
                                </div>

                                {/* Column checklist */}
                                <div
                                    className="border rounded bg-white"
                                    style={{ maxHeight: 220, overflowY: 'auto' }}
                                >
                                    {filteredColumns.length === 0 ? (
                                        <div className="text-center py-3 text-muted small">No columns match your search.</div>
                                    ) : (
                                        filteredColumns.map((col, i) => {
                                            const checked = currentMask.includes(col.name);
                                            const isIdentity = isIdentityField(col.name);
                                            const alias = currentAliases[col.name] || '';
                                            return (
                                                <div
                                                    key={col.id || col.name}
                                                    className="d-flex align-items-center px-3 py-2"
                                                    style={{
                                                        borderBottom: i < filteredColumns.length - 1 ? '1px solid #f1f5f9' : 'none',
                                                        background: checked ? theme.primary + '08' : 'transparent',
                                                        transition: 'background 0.15s',
                                                        gap: 8,
                                                    }}
                                                >
                                                    <input
                                                        type="checkbox"
                                                        className="form-check-input m-0 flex-shrink-0"
                                                        checked={checked}
                                                        onChange={() => toggleField(col.name)}
                                                        style={{ accentColor: theme.primary, cursor: 'pointer' }}
                                                    />
                                                    <div
                                                        className="d-flex align-items-center gap-2 overflow-hidden flex-grow-1"
                                                        style={{ cursor: 'pointer' }}
                                                        onClick={() => toggleField(col.name)}
                                                    >
                                                        <span className="text-truncate fw-medium"
                                                            style={{ fontSize: 13, color: checked ? theme.primary : '#334155' }}>
                                                            {col.name}
                                                        </span>
                                                        {col.data_type && (
                                                            <span style={{ fontSize: 10, fontFamily: 'monospace', color: '#94a3b8', flexShrink: 0 }}>
                                                                {col.data_type}
                                                            </span>
                                                        )}
                                                        {isIdentity && (
                                                            <span style={{
                                                                fontSize: 9, padding: '1px 6px', borderRadius: 20,
                                                                fontWeight: 700, background: '#fef3c7', color: '#b45309',
                                                                letterSpacing: 0.3, flexShrink: 0,
                                                            }}>
                                                                IDENTITY
                                                            </span>
                                                        )}
                                                    </div>
                                                    <div style={{ width: 170, flexShrink: 0 }}>
                                                        {checked ? (
                                                            <div className="input-group input-group-sm">
                                                                <span className="input-group-text"
                                                                    style={{ background: '#f8fafc', fontSize: 11, padding: '0 6px', color: '#94a3b8' }}>
                                                                    <i className="bi bi-arrow-right"></i>
                                                                </span>
                                                                <input
                                                                    type="text"
                                                                    className="form-control form-control-sm"
                                                                    placeholder={col.name}
                                                                    value={alias}
                                                                    onChange={e => updateAlias(col.name, e.target.value)}
                                                                    onClick={e => e.stopPropagation()}
                                                                    style={{
                                                                        fontSize: 12,
                                                                        borderRadius: '0 4px 4px 0',
                                                                        background: alias ? '#fffbeb' : '#fff',
                                                                        color: alias ? '#b45309' : '#334155',
                                                                        fontFamily: alias ? 'monospace' : 'inherit',
                                                                    }}
                                                                    title={alias ? `Will appear as "${alias}" in payload` : 'Leave blank to use original column name'}
                                                                />
                                                            </div>
                                                        ) : (
                                                            <span className="text-muted ps-2" style={{ fontSize: 11 }}>—</span>
                                                        )}
                                                    </div>
                                                </div>
                                            );
                                        })
                                    )}
                                </div>
                                {/* Alias hint banner */}
                                {Object.keys(currentAliases).length > 0 && (
                                    <div className="mt-2 p-2 rounded"
                                        style={{ background: '#fffbeb', border: '1px solid #fde68a', fontSize: 11, color: '#b45309' }}>
                                        <i className="bi bi-info-circle me-1"></i>
                                        <strong>{Object.keys(currentAliases).length} field{Object.keys(currentAliases).length !== 1 ? 's' : ''}</strong> will use a custom key name in the payload.
                                        Share these aliases with the receiving system developer.
                                    </div>
                                )}
                                {/* Footer hint */}
                                {currentMask.length > 0 && (
                                    <div className="mt-2 d-flex flex-wrap gap-1">
                                        {currentMask.slice(0, 6).map(f => (
                                            <span
                                                key={f}
                                                className="badge"
                                                style={{
                                                    background: theme.primary + '15',
                                                    color: theme.primary,
                                                    fontSize: 10, fontWeight: 600, fontFamily: 'monospace',
                                                }}
                                                title={currentAliases[f] ? `Alias: ${currentAliases[f]}` : ''}
                                            >
                                                {currentAliases[f] ? `${f} → ${currentAliases[f]}` : f}
                                            </span>
                                        ))}
                                        {currentMask.length > 6 && (
                                            <span className="badge bg-light text-muted border" style={{ fontSize: 10 }}>
                                                +{currentMask.length - 6} more
                                            </span>
                                        )}
                                    </div>
                                )}
                            </>
                        )}
                    </>
                )}
            </div>
        </div>
    );
}